import {
  BlissfulIcon,
  BlossomIcon,
  Box,
  Button,
  DismissIcon,
  Flex,
  HopIcon,
  Image,
  LemonIcon,
  LightningIcon,
  NotHighIcon,
  PainFreeIcon,
  PencilIcon,
  PepperIcon,
  PizzaIcon,
  RelaxedIcon,
  SleepIcon,
  Typography,
} from '@iheartjane/reefer';
import capitalize from 'lodash-es/capitalize';
import type { ReactNode } from 'react';

import { WEIGHT_OPTIONS } from '../../utils/constants';
import { RatingStars } from '../productReviews/ratingStars/ratingStars';
import { AttributePill } from './attributePill';
import type { CompareProduct } from './compareProvider';

import styles from './compareModal.module.css';

const ICON_SIZE = 'sm';

function formatWeights(product: CompareProduct): string {
  const ids = new Set(
    product.availableWeights.map((weight) => weight.replace(/ /g, '_'))
  );
  const labels = WEIGHT_OPTIONS.filter((weight) => ids.has(weight.id)).map(
    (weight) => weight.label
  );
  if (labels.length <= 1) return labels.join('');
  return `${labels[0]} - ${labels[labels.length - 1]}`;
}

const FEELING_ICONS: Record<string, ReactNode> = {
  Blissful: <BlissfulIcon size={ICON_SIZE} />,
  Creative: <PencilIcon size={ICON_SIZE} />,
  Energetic: <LightningIcon size={ICON_SIZE} />,
  Hungry: <PizzaIcon size={ICON_SIZE} />,
  'Not high': <NotHighIcon size={ICON_SIZE} />,
  'Pain free': <PainFreeIcon size={ICON_SIZE} />,
  Relaxed: <RelaxedIcon size={ICON_SIZE} />,
  Relieved: <PainFreeIcon size={ICON_SIZE} />,
  Sleepy: <SleepIcon size={ICON_SIZE} />,
};

const FLAVOR_ICONS: Record<string, ReactNode> = {
  Citrusy: <LemonIcon size={ICON_SIZE} />,
  Floral: <BlossomIcon size={ICON_SIZE} />,
  Hoppy: <HopIcon size={ICON_SIZE} />,
  Spicy: <PepperIcon size={ICON_SIZE} />,
};

const EmptyCell = () => (
  <Typography color="grays-mid" as="p" aria-label="Not available">
    &mdash;
  </Typography>
);

const renderPotency = (value: number | null) =>
  value == null ? null : <Typography as="p">{value}%</Typography>;

interface CompareTableProps {
  addToCartLabel?: string;
  /**
   * Returns how many of a product are already in the bag. When > 0 the count is
   * appended to the add button (e.g. "Add (1)") so shoppers get confirmation a
   * product was added — Bloom has no slide-out bag to signal it otherwise.
   */
  getCountInCart?: (productId: number) => number;
  isMobile: boolean;
  onAddToCart: (product: CompareProduct) => void;
  onProductClick: (product: CompareProduct) => void;
  onRemove: (id: number) => void;
  products: CompareProduct[];
}

export const CompareTable = ({
  addToCartLabel = 'Add',
  getCountInCart,
  products,
  onRemove,
  onProductClick,
  onAddToCart,
  isMobile,
}: CompareTableProps) => {
  const rows: {
    label: string;
    render: (product: CompareProduct) => React.ReactNode;
  }[] = [
    {
      label: 'Type',
      render: (product) =>
        product.kind ? (
          <Typography as="p">{capitalize(product.kind)}</Typography>
        ) : null,
    },
    {
      label: 'Price',
      render: (product) => {
        if (!product.price) return null;
        const isMultiWeight = product.availableWeights.length > 1;
        if (product.originalPrice) {
          return (
            <Flex
              justifyContent="center"
              gap={4}
              flexDirection={isMultiWeight ? 'column' : 'row'}
              alignItems={isMultiWeight ? 'center' : 'baseline'}
            >
              <Typography
                as="p"
                variant="body-bold"
                className={styles['salePrice']}
              >
                {product.price}
              </Typography>
              <Typography as="p" variant="mini" color="grays-mid" strikeThrough>
                {product.originalPrice}
              </Typography>
            </Flex>
          );
        }
        return (
          <Typography as="p" variant="body-bold">
            {product.price}
          </Typography>
        );
      },
    },
    {
      label: 'Rating',
      render: (product) => {
        if (product.aggregateRating == null) return null;
        return (
          <Flex
            className={styles['ratingWrap']}
            alignItems="center"
            justifyContent="center"
            gap={4}
          >
            <RatingStars rating={product.aggregateRating} />
            <Typography as="p">
              {product.aggregateRating.toFixed(1)}
              {product.reviewCount != null ? ` (${product.reviewCount})` : ''}
            </Typography>
          </Flex>
        );
      },
    },
    {
      label: 'Weight',
      render: (product) => {
        const weights = formatWeights(product);
        return weights ? <Typography as="p">{weights}</Typography> : null;
      },
    },
    {
      label: 'Dosage',
      render: (product) =>
        product.dosage ? (
          <Typography as="p">{product.dosage}</Typography>
        ) : null,
    },
    {
      label: 'THC',
      render: (product) => renderPotency(product.percentThc),
    },
    {
      label: 'CBD',
      render: (product) => renderPotency(product.percentCbd),
    },
    {
      label: 'Strain',
      render: (product) =>
        product.category ? (
          <Typography as="p">{capitalize(product.category)}</Typography>
        ) : null,
    },
    {
      label: 'Top Feelings',
      render: (product) =>
        product.feelings.length > 0 ? (
          <Flex flexWrap="wrap" justifyContent="center" gap={4}>
            {product.feelings.slice(0, 3).map((feeling) => (
              <AttributePill
                key={feeling}
                label={feeling}
                icon={FEELING_ICONS[feeling]}
              />
            ))}
          </Flex>
        ) : null,
    },
    {
      label: 'Top Flavors',
      render: (product) =>
        product.flavors.length > 0 ? (
          <Flex flexWrap="wrap" justifyContent="center" gap={4}>
            {product.flavors.slice(0, 3).map((flavor) => (
              <AttributePill
                key={flavor}
                label={flavor}
                icon={FLAVOR_ICONS[flavor]}
              />
            ))}
          </Flex>
        ) : null,
    },
  ];

  // Hide any row where no compared product has a value; keep the rest and let
  // individual empty cells fall back to "—".
  const visibleRows = rows.filter((row) =>
    products.some((product) => row.render(product) !== null)
  );

  return (
    <table className={styles['table']}>
      <colgroup>
        <col className={styles['labelCol']} />
        {products.map((product) => (
          <col key={product.id} />
        ))}
      </colgroup>
      <thead className={styles['thead']}>
        <tr>
          <th className={styles['labelCell']} />
          {products.map((product) => (
            <th key={product.id}>
              <Box className={styles['productImg']}>
                <Image
                  src={product.imageUrl}
                  altText={product.name}
                  width="100%"
                  height="100%"
                  objectFit="cover"
                  loading="lazy"
                />
                <Button.Icon
                  className={styles['removeBtn']}
                  icon={<DismissIcon size="sm" />}
                  onClick={() => onRemove(product.id)}
                  aria-label={`Remove ${product.name}`}
                  variant="minimal"
                />
              </Box>
              <Button
                className={styles['productName']}
                onClick={() => onProductClick(product)}
                to={product.url ?? undefined}
                aria-label={`View ${product.name}`}
                variant="minimal"
                label={
                  <Typography as="p" variant="mini-bold" truncateAt="160px">
                    {product.name}
                  </Typography>
                }
              />
              {product.brand && (
                <Typography as="p" variant="mini" color="grays-mid">
                  {product.brand}
                </Typography>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {visibleRows.map((row) => (
          <tr key={row.label}>
            <td className={styles['labelCell']}>
              <Typography as="p" variant="mini-bold">
                {row.label}
              </Typography>
            </td>
            {products.map((product) => (
              <td key={product.id}>{row.render(product) ?? <EmptyCell />}</td>
            ))}
          </tr>
        ))}
        <tr className={styles['spacerRow']} aria-hidden="true">
          <td colSpan={products.length + 1} />
        </tr>
      </tbody>
      <tfoot className={styles['tfoot']}>
        <tr>
          <td className={styles['labelCell']} />
          {products.map((product) => {
            const countInCart = getCountInCart?.(product.id) ?? 0;
            return (
              <td key={product.id}>
                <Flex gap={8}>
                  <Button
                    variant="primary"
                    label={
                      countInCart > 0
                        ? `${addToCartLabel} (${countInCart})`
                        : addToCartLabel
                    }
                    aria-label={`Add ${product.name} to bag`}
                    onClick={() => onAddToCart(product)}
                    full
                  />
                  {!isMobile && (
                    <Button
                      variant="tertiary"
                      label="View"
                      onClick={() => onProductClick(product)}
                      to={product.url ?? undefined}
                      full
                    />
                  )}
                </Flex>
              </td>
            );
          })}
        </tr>
      </tfoot>
    </table>
  );
};
